/**
 * PostHog React Error Boundary
 *
 * Catches unhandled component render crashes, logs the full stack trace
 * to PostHog, associates it with the user session, and offers a clean recovery UI.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureException, captureEvent } from '../../lib/analytics/posthog';
import { Button } from '../primitives/Button';
import { t } from '../../lib/i18n/t';
import '../../styles/components/error-block.css';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    captureException(error, {
      component_stack: errorInfo.componentStack,
      screen_path: window.location.pathname,
    });
  }

  handleRetry = () => {
    captureEvent('error_boundary_retry_clicked', {
      screen_path: window.location.pathname,
    });
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="err" role="alert" style={{ margin: 'var(--space-8) auto', maxWidth: 640 }}>
          <span className="err__glyph" aria-hidden="true">
            <svg viewBox="0 0 16 16" width="16" height="16" focusable="false">
              <path
                d="M8 1.5L15 14H1L8 1.5Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
              <path d="M8 6V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="8" cy="11.5" r="0.75" fill="currentColor" />
            </svg>
          </span>
          <div className="err__content">
            <h2 className="err__title">
              {this.props.fallbackTitle ?? t('Something went wrong on this screen')}
            </h2>
            <p className="err__desc">
              {this.state.error?.message ||
                t('An unexpected error occurred. This crash has been logged to telemetry.')}
            </p>
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Button variant="solid" onClick={this.handleRetry}>
                {t('Try again')}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
